import { useCallback, useEffect, useState } from 'react';
import type { Api, IysSettings, IysView, RetentionView } from '../api';
import type { T } from '../i18n';
import { DangerButton, Field } from '../ui';
import { errorText } from './AgentSettings';

type Days = { recordingsDays: number; transcriptsDays: number; messagesDays: number };

/** ADR-071 U2 (BRAIN/31 C2): how long the business keeps call recordings, call content and messages. */
export const PrivacyPage = ({ api, t }: { api: Api; t: T }) => {
  const [view, setView] = useState<RetentionView>();
  const [x, setX] = useState<Days>({ recordingsDays: 90, transcriptsDays: 365, messagesDays: 365 });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string }>();
  const [phone, setPhone] = useState('');
  const [toDnc, setToDnc] = useState(true);
  const [erased, setErased] = useState<string>();

  const load = useCallback(
    () =>
      api
        .retention()
        .then((v) => {
          setView(v);
          setX({ recordingsDays: v.recordingsDays, transcriptsDays: v.transcriptsDays, messagesDays: v.messagesDays });
        })
        .catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) })),
    [api, t],
  );
  useEffect(() => void load(), [load]);

  const save = async () => {
    setBusy(true);
    setMsg(undefined);
    try {
      await api.saveRetention(x);
      setMsg({ kind: 'ok', text: t('privacy.saved') });
      void load();
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const field = (k: keyof Days, label: string, hint: string) => (
    <Field label={label} hint={hint}>
      <input
        type="number"
        min={view?.minDays ?? 30}
        max={view?.maxDays ?? 730}
        value={x[k]}
        onChange={(e) => setX({ ...x, [k]: Number(e.target.value) })}
      />
    </Field>
  );
  const erase = async () => {
    setBusy(true);
    setMsg(undefined);
    setErased(undefined);
    try {
      const r = await api.erasePerson(phone.trim(), toDnc);
      setErased(t('privacy.erase.done', { calls: r.calls, recordings: r.recordings, messages: r.messages, contacts: r.contacts }));
      setPhone('');
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  const valid = view !== undefined && Object.values(x).every((d) => Number.isInteger(d) && d >= view.minDays && d <= view.maxDays);

  return (
    <section>
      <header className="page-head">
        <div>
          <h1>{t('privacy.title')}</h1>
          <p className="muted">{t('privacy.subtitle')}</p>
        </div>
      </header>
      {msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      {view && (
        <div className="card line-form">
          <h3>{t('privacy.retention')}</h3>
          <p className="hint">{t('privacy.retention.hint', { min: view.minDays, max: view.maxDays })}</p>
          {field('recordingsDays', t('privacy.recordings'), t('privacy.recordings.hint', { n: view.defaults.recordingsDays }))}
          {field('transcriptsDays', t('privacy.transcripts'), t('privacy.transcripts.hint', { n: view.defaults.transcriptsDays }))}
          {field('messagesDays', t('privacy.messages'), t('privacy.messages.hint', { n: view.defaults.messagesDays }))}
          <p className="hint">{t('privacy.kept')}</p>
          <div className="row-between section-gap">
            <span className="hint">{view.custom && view.updatedAt ? t('privacy.updated', { at: new Date(view.updatedAt).toLocaleString() }) : t('privacy.usingDefaults')}</span>
            <button type="button" className="primary" disabled={busy || !valid} onClick={() => void save()}>{busy ? t('settings.saving') : t('privacy.save')}</button>
          </div>
        </div>
      )}
      <IysCard api={api} t={t} />
      {view && (
        <div className="card line-form section-gap">
          <h3>{t('privacy.erase.title')}</h3>
          <p className="hint">{t('privacy.erase.hint')}</p>
          <Field label={t('privacy.erase.phone')} hint={t('sms.test.to.hint')}>
            <input value={phone} inputMode="tel" placeholder="+90…" onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <label className="row"><input type="checkbox" checked={toDnc} onChange={(e) => setToDnc(e.target.checked)} /> {t('privacy.erase.dnc')}</label>
          {erased && <div className="banner ok small">{erased}</div>}
          <div className="row-between section-gap">
            <span className="hint">{t('privacy.erase.warn')}</span>
            {phone.trim().length >= 8 && <DangerButton t={t} label={t('privacy.erase.button')} onConfirm={() => void erase()} />}
          </div>
        </div>
      )}
    </section>
  );
};

/** BRAIN/35 Faz 5: the business's İYS check before commercial calls (off until turned on) and this month's counts. */
const IysCard = ({ api, t }: { api: Api; t: T }) => {
  const [view, setView] = useState<IysView>();
  const [x, setX] = useState<IysSettings>({ checkEnabled: false, retentionDays: 7, skipBusiness: true });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string }>();
  const load = useCallback(
    () =>
      api
        .iys()
        .then((v) => {
          setView(v);
          setX({ checkEnabled: v.checkEnabled, retentionDays: v.retentionDays, skipBusiness: v.skipBusiness });
        })
        .catch((e: unknown) => setMsg({ kind: 'error', text: errorText(t, e) })),
    [api, t],
  );
  useEffect(() => void load(), [load]);
  const save = async () => {
    setBusy(true);
    setMsg(undefined);
    try {
      await api.saveIys(x);
      setMsg({ kind: 'ok', text: t('privacy.iys.saved') });
      void load();
    } catch (e) {
      setMsg({ kind: 'error', text: errorText(t, e) });
    } finally {
      setBusy(false);
    }
  };
  if (!view) return msg ? <div className={`banner ${msg.kind} small section-gap`}>{msg.text}</div> : null;
  const money = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const valid = Number.isInteger(x.retentionDays) && x.retentionDays >= 0 && x.retentionDays <= 30;
  const month = new Date(`${view.stats.month}T00:00:00Z`).toLocaleDateString(undefined, { year: 'numeric', month: 'long', timeZone: 'UTC' });
  return (
    <div className="card line-form section-gap">
      <h3>{t('privacy.iys.title')}</h3>
      <p className="hint">{t('privacy.iys.hint')}</p>
      {!view.provider && <div className="banner info small">{t('privacy.iys.noProvider', { price: money(view.queryPriceTry) })}</div>}
      {view.provider?.name === 'mock' && <div className="banner warn small">{t('privacy.iys.mock')}</div>}
      {view.provider && view.provider.name !== 'mock' && <p className="hint">{t('privacy.iys.provider', { name: view.provider.name, price: money(view.provider.priceTry) })}</p>}
      <label className="row">
        <input type="checkbox" checked={x.checkEnabled} disabled={!view.provider && !x.checkEnabled} onChange={(e) => setX({ ...x, checkEnabled: e.target.checked })} /> {t('privacy.iys.enabled')}
      </label>
      <Field label={t('privacy.iys.days')} hint={t('privacy.iys.days.hint')}>
        <input type="number" min={0} max={30} value={x.retentionDays} onChange={(e) => setX({ ...x, retentionDays: Number(e.target.value) })} />
      </Field>
      <label className="row"><input type="checkbox" checked={x.skipBusiness} onChange={(e) => setX({ ...x, skipBusiness: e.target.checked })} /> {t('privacy.iys.skipBusiness')}</label>
      <p className="hint">{t('privacy.iys.skipBusiness.hint')}</p>
      <h4 className="section-gap">{t('privacy.iys.stats', { month })}</h4>
      <div className="kpis four">
        <div className="kpi static"><span className="kpi-label">{t('privacy.iys.stats.queries')}</span><span className="kpi-value">{view.stats.queries}</span></div>
        <div className="kpi static"><span className="kpi-label">{t('privacy.iys.stats.cached')}</span><span className="kpi-value">{view.stats.cached}</span></div>
        <div className="kpi static"><span className="kpi-label">{t('privacy.iys.stats.business')}</span><span className="kpi-value">{view.stats.businessSkips}</span></div>
        <div className="kpi static"><span className="kpi-label">{t('privacy.iys.stats.spent')}</span><span className="kpi-value">{t('privacy.iys.money', { amount: money(view.stats.spentTry) })}</span></div>
      </div>
      {msg && <div className={`banner ${msg.kind} small`}>{msg.text}</div>}
      <div className="row-between section-gap">
        <span className="hint">{view.updatedAt ? t('privacy.updated', { at: new Date(view.updatedAt).toLocaleString() }) : t('privacy.iys.defaults')}</span>
        <button type="button" className="primary" disabled={busy || !valid} onClick={() => void save()}>{busy ? t('settings.saving') : t('privacy.save')}</button>
      </div>
    </div>
  );
};
